import { createLogger } from '../utils/logger';
const log = createLogger('ThemeManager');
/**
 * Theme Manager - Handles light/dark mode switching
 */

export type Theme = 'light' | 'dark';

class ThemeManager {
    private currentTheme: Theme;
    private readonly STORAGE_KEY = 'oi-cdn-theme';

    constructor() {
        // Check for saved theme or use system preference
        this.currentTheme = this.getSavedTheme() || this.getSystemPreference();
        this.applyTheme(this.currentTheme);

        // Listen for system theme changes
        this.watchSystemTheme();
    }

    /**
     * Get the current theme
     */
    getTheme(): Theme {
        return this.currentTheme;
    }

    /**
     * Set and apply a theme
     */
    setTheme(theme: Theme): void {
        this.currentTheme = theme;
        this.applyTheme(theme);
        this.saveTheme(theme);
        this.notifyListeners(); // Notify subscribers
    }

    /**
     * Toggle between light and dark mode
     */
    toggleTheme(): void {
        const newTheme: Theme = this.currentTheme === 'light' ? 'dark' : 'light';
        this.setTheme(newTheme);
    }

    /**
     * Apply theme to the document
     */
    private applyTheme(theme: Theme): void {
        document.documentElement.setAttribute('data-theme', theme);
    }

    /**
     * Save theme to localStorage
     */
    private saveTheme(theme: Theme): void {
        try {
            localStorage.setItem(this.STORAGE_KEY, theme);
        } catch (error) {
            log.warn('Failed to save theme preference:', error);
        }
    }

    /**
     * Get saved theme from localStorage
     */
    private getSavedTheme(): Theme | null {
        try {
            const saved = localStorage.getItem(this.STORAGE_KEY);
            if (saved === 'light' || saved === 'dark') {
                return saved;
            }
        } catch (error) {
            log.warn('Failed to load theme preference:', error);
        }
        return null;
    }

    /**
     * Get system color scheme preference
     */
    private getSystemPreference(): Theme {
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return 'dark';
        }
        return 'light';
    }

    /**
     * Watch for system theme changes
     */
    private watchSystemTheme(): void {
        if (!window.matchMedia) return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        // Only update if user hasn't manually set a preference
        mediaQuery.addEventListener('change', (e) => {
            if (!this.getSavedTheme()) {
                this.setTheme(e.matches ? 'dark' : 'light');
            }
        });
    }

    // === Subscription System ===
    private listeners: ((theme: Theme) => void)[] = [];

    /**
     * Subscribe to theme changes
     * @param callback Function to call when theme changes
     * @returns Unsubscribe function
     */
    subscribe(callback: (theme: Theme) => void): () => void {
        this.listeners.push(callback);
        // Call immediately with current theme
        callback(this.currentTheme);

        return () => {
            this.listeners = this.listeners.filter(l => l !== callback);
        };
    }

    private notifyListeners(): void {
        this.listeners.forEach(listener => listener(this.currentTheme));
    }
}

// Export singleton instance
export const themeManager = new ThemeManager();
